import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { css } from "styled-components/macro"; //eslint-disable-line
import {DataGrid, GridCellModes, GridToolbarContainer, GridToolbarColumnsButton,
  GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport} from '@mui/x-data-grid';
import {Alert, Button, Box, Chip, Container, Card, CardContent, Grid, Stack, Typography} from '@mui/material';
import {People, Schedule} from '@mui/icons-material';
// import {PersonAdd, Business} from '@mui/icons-material';
import AnimationRevealPage from "components/AnimationRevealPage.js";
import Header from "components/Header.js";
import {fetchUsers, updateUser} from "helpers/userManagement.js";
import AuthContext from 'contexts/AuthContext';

const ROLE_OPTIONS = ["MEMBER", "EXECUTIVE", "ADMIN"];
// Sub-roles that only narrow an EXECUTIVE's access - meaningless on any other
// role. "" (shown as "None") means full EXECUTIVE access, same as PRESIDENT.
const DESIGNATION_OPTIONS = [
  {value: "", label: "None"},
  {value: "TREASURER", label: "Treasurer"},
  {value: "SECRETARY", label: "Secretary"},
  {value: "PRESIDENT", label: "President"}
];

const numComparator = (v1, v2) => parseInt(v1) - parseInt(v2);

export default function UserManagement() {
  const {role} = useContext(AuthContext);
  const [cellModesModel, setCellModesModel] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userSummary, setUserSummary] = useState({total_users: 0, self_occupied: 0,
    rented_out: 0, vacant: 0, last_updated: new Date()});

  // Other than USER all other roles have read permission
  const hasPermission = useMemo(() => (role && role !== "USER" && role !== "REGISTER"), [role]);
  const isAdmin = useMemo(() => role === "ADMIN", [role]);

  // Memoize columns definition for user data
  const columns = useMemo(() => [
    {field: 'userRef', headerName: 'User ID', width: 150, editable: false},
    {field: 'plotNumber', headerName: 'Plot #', width: 80, editable: isAdmin,
      align: 'center', sortComparator: numComparator},
    {field: 'name', headerName: 'Full Name', minWidth: 150, flex: 1, editable: isAdmin},
    {field: 'phoneNumber', headerName: 'Phone', width: 130, editable: isAdmin, align: 'right'},
    {field: 'email', headerName: 'Email', minWidth: 200, flex: 1, editable: isAdmin},
    {field: 'firstName', headerName: 'First Name', width: 120, editable: isAdmin},
    {field: 'lastName', headerName: 'Last Name', width: 120, editable: isAdmin},
    {field: 'isSelfOccupied', headerName: 'Self Occupied', width: 120, type: 'boolean', editable: isAdmin,
      renderCell: (params) => (<Chip label={params.value ? 'Yes' : 'No'}
        color={params.value ? 'success' : 'default'} size="small"/>)
    },
    {field: 'street', headerName: 'Street', width: 120, editable: isAdmin},
    {field: 'sector', headerName: 'Sector', width: 100, editable: isAdmin},
    {field: 'city', headerName: 'City', width: 100, editable: isAdmin},
    {field: 'pinCode', headerName: 'Pincode', width: 100, editable: isAdmin},
    {field: 'state', headerName: 'State', width: 100, editable: isAdmin},
    {field: 'country', headerName: 'Country', width: 100, editable: isAdmin},
    {field: 'tenantName', headerName: 'Tenant Name', width: 130, editable: isAdmin},
    {field: 'tenantPhone', headerName: 'Tenant Phone', width: 130, editable: isAdmin},
    {field: 'moveInDate', headerName: 'Move In Date', width: 120, type: 'date', editable: isAdmin,
      valueGetter: (params) => params.row?.moveInDate ? new Date(params.row.moveInDate) : null},
    {field: 'role', headerName: 'Role', width: 150, type: "singleSelect",
      valueOptions: ROLE_OPTIONS, editable: isAdmin},
    {field: 'designation', headerName: 'Office', width: 150, type: "singleSelect",
      valueOptions: DESIGNATION_OPTIONS, editable: isAdmin,
      // Only meaningful on an EXECUTIVE - other roles can't have an office.
      isCellEditable: (params) => isAdmin && params.row.role === "EXECUTIVE"},
    {field: 'updatedAt', headerName: 'Updated At', width: 150, type: 'date',
      valueGetter: (params) => params.row?.updatedAt ? new Date(params.row.updatedAt) : new Date()}
  ], [isAdmin]);

  // Optimize cell click handler
  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable || (event.target?.nodeType === 1 && !event.currentTarget.contains(event.target))) {
      return;
    }
    setCellModesModel((prevModel) => {
      const newModel = Object.keys(prevModel).reduce((acc, id) => ({
        ...acc,
        [id]: Object.keys(prevModel[id]).reduce((acc2, field) => ({
          ...acc2,
          [field]: { mode: GridCellModes.View }
        }), {})
      }), {});
      return {
        ...newModel,
        [params.id]: {
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit }
        }
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel) => {
    // cellModesModel is controlled, so MUI's own edit-start paths (double-click,
    // Enter/keyboard) land here too, not just handleCellClick - without this check
    // they'd bypass each column's editable/isCellEditable and open e.g. the Office
    // dropdown on a MEMBER row.
    const sanitized = {};
    for (const [id, fields] of Object.entries(newModel)) {
      const row = users.find((u) => u.userRef === id);
      const sanitizedFields = {};
      for (const [field, cellMode] of Object.entries(fields)) {
        if (cellMode.mode === GridCellModes.Edit) {
          const col = columns.find((c) => c.field === field);
          const isEditable = row && col?.editable &&
            (!col.isCellEditable || col.isCellEditable({row}));
          if (!isEditable) {
            continue;
          }
        }
        sanitizedFields[field] = cellMode;
      }
      sanitized[id] = sanitizedFields;
    }
    setCellModesModel(sanitized);
  }, [users, columns]);

  const handleRowUpdate = useCallback((updatedRow, originalRow) => {
    setUsers(prev => prev.map(user =>
      user.userRef === updatedRow.userRef
        ? { ...updatedRow, isEdited: true}
        : user
    ));
    return updatedRow;
  }, []);

  const handleAddEntry = useCallback(() => {
    const newUser = {
      userRef: `new_${Date.now()}`, email: "", name: "", firstName: "",
      lastName: "", phoneNumber: "", plotNumber: "", street: "",
      sector: "", city: "", isSelfOccupied: true, tenantName: "",
      tenantPhone: "", moveInDate: null, role: "MEMBER", designation: "", isEdited: true};
    setUsers(prev => [...prev, newUser]);
  }, []);

  // Calculate user summary
  const calculateUserSummary = useCallback((usersList) => {
    const summary = usersList.reduce((acc, user) => {
      if (user.role === "MEMBER") {
        acc.total_users++;
        if (user.isSelfOccupied) {
          acc.self_occupied++;
        } else if (user.tenantName) {
          acc.rented_out++;
        } else {
          acc.vacant++;
        }
      }
      return acc;
    }, {
      total_users: 0,
      self_occupied: 0,
      rented_out: 0,
      vacant: 0,
      last_updated: new Date()
    });

    setUserSummary(summary);
  }, []);

  // Load users with error handling
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchUsers();
      if (response) {
        const usersList = Array.isArray(response) ? response : [];
        setUsers(usersList);
        calculateUserSummary(usersList);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [calculateUserSummary]);

  const handleSubmit = useCallback(async () => {
    if (!isAdmin) return;

    setSaving(true);
    setError(null);

    try {
      const editedUsers = users.filter(user => user.isEdited);
      const request = editedUsers.map(user => ({
        userRef: user.userRef.startsWith('new_') ? undefined : user.userRef,
        email: user.email, name: user.name, firstName: user.firstName,
        lastName: user.lastName, phoneNumber: user.phoneNumber,
        plotNumber: user.plotNumber, street: user.street, sector: user.sector,
        city: user.city, isSelfOccupied: user.isSelfOccupied,
        tenantName: user.tenantName, tenantPhone: user.tenantPhone,
        moveInDate: user.moveInDate ? dayjs(user.moveInDate).toISOString() : null,
        role: user.role,
        // Backend rejects a non-empty designation unless role is EXECUTIVE, and
        // only an ADMIN caller may set one at all - both enforced server-side.
        designation: user.role === "EXECUTIVE" ? (user.designation || "") : ""
      }));
      await updateUser(request);
      await loadUsers();
    } catch (err) {
      console.error('Error saving users:', err);
      setError('Failed to save users. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [users, loadUsers, isAdmin]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    loadUsers();
  }, [loadUsers]);

  const hasChanges = useMemo(() => {
    return users.some(user => user.isEdited);
  }, [users]);

  // Load data on component mount
  useEffect(() => {
    if (hasPermission) {
      loadUsers();
    }
  }, [loadUsers, hasPermission]);

  // Early return for permission check
  if (!hasPermission) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <Alert severity="error">Permission denied. You need appropriate permissions to view user management.</Alert>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container style={{padding: 15, marginTop: 40}}>
        {error && (
          <Alert severity="error" style={{marginBottom: 20}}>
            {error}
          </Alert>
        )}
        {/* User Statistics Cards */}
        <Grid container spacing={2} style={{ marginBottom: 50 }}>
          <div style={{width: 220}}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Members
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {userSummary.total_users}
                    </Typography>
                  </Box>
                  <People color="primary" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </div>
          {/* <div style={{width: 220}}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Occupied
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {userSummary.self_occupied}
                    </Typography>
                  </Box>
                  <Business color="success" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </div>
          <div style={{width: 220}}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Rented Out
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {userSummary.rented_out}
                    </Typography>
                  </Box>
                  <PersonAdd color="info" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </div> */}
          <div style={{width: 300}}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Last Updated
                    </Typography>
                    <div style={{display: 'flex', flexDirection: 'row'}}>
                      <Typography variant="h6" style={{marginRight: 10, fontWeight: 100}}>
                        {dayjs(userSummary.last_updated).format('MMM DD, YYYY')}
                      </Typography>
                      <Chip size="small" color="primary" variant="outlined"
                        label={dayjs(userSummary.last_updated).format('h:mm A')} />
                    </div>
                  </Box>
                  <Schedule color="primary" fontSize="large" />
                </Box>
              </CardContent>
            </Card>
          </div>
        </Grid>
        {/* User Data Grid */}
        <DataGrid autoHeight loading={loading} showToolbar
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#575757",
              whiteSpace: "normal",
              lineHeight: "normal",
              fontWeight: 600,
            },
            "& .MuiDataGrid-columnHeaders": {
              maxHeight: "168px !important"
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none"
            }
          }}
          initialState={{
            sorting: { sortModel: [{field: 'plotNumber'}] },
            columns: {
              columnVisibilityModel: {
                "userRef": false, "firstName": false, "lastName": false,
                "tenantName": false, "tenantPhone": false, "street": false,
                "sector": false, "city": false, "pinCode": false, "state": false,
                "country": false, "moveInDate": false, "updatedAt": false,
                "isSelfOccupied": false, "email": false, "role": false, "designation": false
              }
            }
          }}
          rows={users}
          getRowId={(row) => row.userRef}
          columns={columns} slots={{toolbar: DataGridToolbar}}
          cellModesModel={cellModesModel}
          onCellModesModelChange={handleCellModesModelChange}
          onCellClick={handleCellClick}
          disableRowSelectionOnClick
          processRowUpdate={handleRowUpdate}
        />
        {/* Action Buttons */}
        <Stack direction="row" spacing={1} style={{marginTop: 20}}>
          {isAdmin && (
            <Button color="primary"
              variant="contained"
              onClick={handleAddEntry}
              disabled={saving}
            >
              Add User
            </Button>
          )}

          <Button
            color="secondary"
            style={{ marginLeft: 'auto' }}
            variant="outlined"
            onClick={handleCancel}
            disabled={saving}
          >
            Refresh
          </Button>

          {isAdmin && (
            <Button
              color="success"
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Stack>
      </Container>
    </AnimationRevealPage>
  );
}

function DataGridToolbar() {
  return (
    <GridToolbarContainer style={{padding: "10px 4px"}}>
      <Typography color="primary" variant="h6" style={{marginLeft: 10, marginRight: 'auto'}}>Members</Typography>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}
